import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { openWhatsapp } from "@/lib/cima";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteDialog({ open, onOpenChange, defaultProduct = "" }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    product_interest: defaultProduct,
    message: "",
  });

  useEffect(() => {
    setForm((f) => ({ ...f, product_interest: defaultProduct }));
  }, [defaultProduct, open]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) return "Por favor escribe tu nombre.";
    if (!form.phone.trim() || form.phone.trim().length < 7)
      return "Ingresa un teléfono válido.";
    if (!form.message.trim()) return "Cuéntanos qué necesitas cotizar.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        product_interest: form.product_interest?.trim() || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/quotes`, payload);
      toast.success("¡Cotización enviada! Te contactaremos muy pronto.");
      setForm({ name: "", phone: "", email: "", product_interest: "", message: "" });
      onOpenChange(false);
    } catch (e2) {
      console.error(e2);
      toast.error(
        "No pudimos enviar tu cotización. Por favor intenta por WhatsApp o vuelve a intentarlo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendByWhatsapp = () => {
    const msg = `Hola CIMA, soy ${form.name || "[mi nombre]"}.
Tel: ${form.phone || "[mi teléfono]"}
${form.product_interest ? `Producto/servicio: ${form.product_interest}\n` : ""}Mensaje: ${form.message || "Quiero solicitar una cotización."}`;
    openWhatsapp(msg);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="sm:max-w-lg border border-slate-300 rounded-none p-0 bg-white"
      >
        <div className="stripe-accent h-2 w-full" />
        <div className="p-6">
          <DialogHeader>
            <DialogTitle
              data-testid="quote-dialog-title"
              className="font-display text-2xl text-[var(--cima-navy)]"
            >
              Solicitar Cotización
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Déjanos tus datos y te respondemos lo antes posible.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="q-name" className="text-xs font-mono-cima uppercase tracking-widest text-slate-600">
                  Nombre *
                </Label>
                <Input
                  id="q-name"
                  data-testid="quote-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Tu nombre"
                  className="rounded-none border-slate-300 mt-1.5 focus-visible:ring-[var(--cima-red)]"
                />
              </div>
              <div>
                <Label htmlFor="q-phone" className="text-xs font-mono-cima uppercase tracking-widest text-slate-600">
                  Teléfono *
                </Label>
                <Input
                  id="q-phone"
                  data-testid="quote-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="755 000 0000"
                  className="rounded-none border-slate-300 mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="q-email" className="text-xs font-mono-cima uppercase tracking-widest text-slate-600">
                Correo (opcional)
              </Label>
              <Input
                id="q-email"
                data-testid="quote-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="tu@correo.com"
                className="rounded-none border-slate-300 mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="q-product" className="text-xs font-mono-cima uppercase tracking-widest text-slate-600">
                Producto o servicio de interés
              </Label>
              <Input
                id="q-product"
                data-testid="quote-input-product"
                value={form.product_interest}
                onChange={update("product_interest")}
                placeholder="Ej. Oxígeno industrial, Argón, Renta de cilindros..."
                className="rounded-none border-slate-300 mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="q-msg" className="text-xs font-mono-cima uppercase tracking-widest text-slate-600">
                Mensaje *
              </Label>
              <Textarea
                id="q-msg"
                data-testid="quote-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Cuéntanos qué necesitas, volumen, frecuencia, etc."
                rows={4}
                className="rounded-none border-slate-300 mt-1.5"
              />
            </div>

            <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-2">
              <button
                type="button"
                data-testid="quote-whatsapp-button"
                onClick={sendByWhatsapp}
                className="btn-cima-whatsapp px-5 py-3 text-sm font-semibold uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Enviar por WhatsApp
              </button>
              <button
                type="submit"
                data-testid="quote-submit-button"
                disabled={submitting}
                className="btn-cima-red px-5 py-3 text-sm font-semibold uppercase tracking-wide flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Cotización
                  </>
                )}
              </button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
